import { useEffect, useRef, useState } from 'react';
import { AudioPlayer } from '../design';
import { streamUrl } from '../lib/audio';
import type { AudioItem } from '../lib/types';

/**
 * Owns the real <audio> element; the design-system AudioPlayer stays a pure
 * shell (CalEmbed precedent for feature components). Media state lives here so
 * timeupdate's ~4 re-renders/second never reach the screen.
 *
 * The stream URL is same-origin, so the session cookie flows with the media
 * request — no crossOrigin attribute, no CORS.
 */

interface AudioPlaybackProps {
  file: Pick<AudioItem, 'id' | 'title' | 'durationSec'>;
  subtitle?: string;
  playing: boolean;
  onToggle: () => void;
  onError: (message: string) => void;
}

export function AudioPlayback({ file, subtitle, playing, onToggle, onError }: AudioPlaybackProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [position, setPosition] = useState(0);
  const [mediaDuration, setMediaDuration] = useState<number | null>(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Swap sources when the screen picks a different program.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setPosition(0);
    setMediaDuration(null);
    audio.load();
    // play() runs synchronously in the click-driven render path — awaiting
    // metadata first would break iOS Safari's user-gesture requirement.
    if (playing) void audio.play().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);

  // Pause/resume without unmounting, so position survives.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) void audio.play().catch(() => undefined);
    else audio.pause();
  }, [playing]);

  const duration = file.durationSec ?? mediaDuration ?? 0;

  return (
    <>
      <audio
        ref={audioRef}
        src={streamUrl(file.id)}
        preload="metadata"
        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          if (Number.isFinite(e.currentTarget.duration)) setMediaDuration(e.currentTarget.duration);
        }}
        onEnded={onToggle}
        onError={() => onErrorRef.current('Could not play this program.')}
      />
      <AudioPlayer
        variant="brand"
        title={file.title}
        subtitle={subtitle}
        duration={duration}
        position={position}
        playing={playing}
        onToggle={onToggle}
        onSeek={(seconds) => {
          const audio = audioRef.current;
          if (!audio) return;
          audio.currentTime = seconds;
          setPosition(seconds);
        }}
      />
    </>
  );
}
